import { Button } from '@/components/ui/button';
import { Currency } from './types';
import { Printer, Copy, Check, X, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface PrintSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  currencySymbol: string;
  currencyCode: Currency;
  modelName: string;
  printHours: number;
  printMinutes: number;
  totalGrams: number;
  materialNames: string;
  totalCost: number;
  totalPrice: number;
  priceWithCommission: number;
  commissionPercent: number;
}

export default function PrintSummaryModal({
  isOpen,
  onClose,
  currencySymbol,
  currencyCode,
  modelName,
  printHours,
  printMinutes,
  totalGrams,
  materialNames,
  totalCost: _totalCost,
  totalPrice,
  priceWithCommission,
  commissionPercent,
}: PrintSummaryModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const formattedTotalPrice = `${currencySymbol} ${totalPrice.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const formattedPriceWithCommission = `${currencySymbol} ${priceWithCommission.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const handlePrint = () => {
    window.print();
  };

  const handleCopyWhatsApp = () => {
    const text = `*PRESUPUESTO DE IMPRESIÓN 3D* 🖨️
*Mila Design Studio*
────────────────────────────
📦 *Pieza:* ${modelName || 'Pieza Personalizada'}
🧵 *Material:* ${materialNames || 'PLA+'}
⚖️ *Peso aprox:* ${totalGrams}g
⏱️ *Tiempo estimado:* ${printHours}h ${printMinutes}m
────────────────────────────
💰 *Precio Final Efectivo / Transferencia:* *${formattedTotalPrice}*
💳 *Precio en plataformas (+${commissionPercent}%):* ${formattedPriceWithCommission}
────────────────────────────
✨ *Garantía de calidad Mila Design*
_Validez del presupuesto: 7 días corridos._`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('¡Presupuesto copiado al portapapeles para WhatsApp!');
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="relative w-full max-w-lg bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
        {/* Header */}
        <div className="bg-primary/10 border-b border-border/80 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-lg text-foreground">Presupuesto de Cotización</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full h-8 w-8">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content (printable) */}
        <div className="p-6 space-y-6 printable-area bg-card">
          <div className="flex items-start justify-between border-b border-border pb-4">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-primary">MILA DESIGN</h2>
              <p className="text-xs text-muted-foreground">Taller de Diseño & Fabricación 3D</p>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <p>Fecha: {new Date().toLocaleDateString('es-AR')}</p>
              <p>Moneda: {currencyCode}</p>
            </div>
          </div>

          <div className="space-y-3 bg-accent/30 rounded-xl p-4 border border-border/60 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Modelo / Pieza:</span>
              <span className="font-semibold text-foreground">{modelName || 'Pieza 3D personalizada'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Material:</span>
              <span className="font-medium text-foreground">{materialNames || 'PLA+'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Peso Estimado:</span>
              <span className="font-mono font-medium text-foreground">{totalGrams} gramos</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tiempo de Impresión:</span>
              <span className="font-mono font-medium text-foreground">{printHours}h {printMinutes}m</span>
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex justify-between items-baseline p-4 rounded-xl bg-primary/10 border border-primary/20">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">Total a Cobrar</p>
                <p className="text-xs text-muted-foreground">Efectivo / Transferencia directa</p>
              </div>
              <span className="text-2xl font-black text-primary font-mono">
                {formattedTotalPrice}
              </span>
            </div>

            {commissionPercent > 0 && (
              <div className="flex justify-between items-center px-4 py-2 text-xs text-muted-foreground">
                <span>Precio con comisión de plataforma (+{commissionPercent}%):</span>
                <span className="font-bold text-foreground font-mono">{formattedPriceWithCommission}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-muted/30 border-t border-border px-6 py-4 flex items-center justify-end gap-3">
          <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Imprimir
          </Button>

          <Button size="sm" onClick={handleCopyWhatsApp} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copiar para WhatsApp
          </Button>
        </div>
      </div>
    </div>
  );
}
